import React from 'react';
import styled from '@emotion/styled';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { PICKER_COLOR } from '@/components/common/datePicker/pickerTokens';
import { addMonths, formatDateOnly, formatDayKo, formatMonthKo, monthCells, monthKey, monthKeyOfDate, parseLocal } from '@/components/common/datePicker/pickerUtils';

/** 달력 색 — 시안 calmock(일 빨강·토 파랑·못 고르는 날 흐리게) */
const CAL_COLOR = {
  sunday: '#E11D48',
  saturday: '#2563EB',
  off: '#C7CFCC',
} as const;

const WEEK_LABELS: string[] = ['일', '월', '화', '수', '목', '금', '토'];

const MonthRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2px 0 6px;

  /* 작은 창 안 좁은 PC 팝업(FilterPanel data-popover-narrow) — 촘촘하게 */
  [data-popover-narrow] & {
    margin: 0 0 4px;
  }

  b {
    font-size: 16px;
    font-weight: 800;
    color: ${PICKER_COLOR.text};
  }
`;

const MonthButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: none;
  color: ${PICKER_COLOR.sub};
  cursor: pointer;

  &:disabled {
    color: ${CAL_COLOR.off};
    cursor: default;
  }

  svg {
    font-size: 24px;
  }

  [data-popover-narrow] & {
    width: 32px;
    height: 32px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  text-align: center;
`;

const WeekLabel = styled.div<{ dow: number }>`
  padding: 4px 0 6px;
  font-size: 12px;
  color: ${({ dow }) => (dow === 0 ? CAL_COLOR.sunday : dow === 6 ? CAL_COLOR.saturday : PICKER_COLOR.muted)};

  [data-popover-narrow] & {
    padding: 2px 0 4px;
  }
`;

type BandSide = 'none' | 'full' | 'right' | 'left';

/** 한 칸 — band 는 기간 선택 때 사이 날짜 옅은 청록 띠(시작일은 오른쪽 반, 종료일은 왼쪽 반) */
const DayCell = styled.div<{ band: BandSide }>`
  position: relative;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;

  [data-popover-narrow] & {
    height: 32px;
  }

  &::before {
    content: '';
    display: ${({ band }) => (band === 'none' ? 'none' : 'block')};
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: ${({ band }) => (band === 'right' ? '50%' : '0')};
    right: ${({ band }) => (band === 'left' ? '50%' : '0')};
    background: ${PICKER_COLOR.primarySoft};
  }
`;

const DayButton = styled.button<{ dow: number; picked: boolean; today: boolean; dot: boolean }>`
  position: relative;
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ picked }) => (picked ? 800 : 500)};
  color: ${({ picked, dow }) => (picked ? '#fff' : dow === 0 ? CAL_COLOR.sunday : dow === 6 ? CAL_COLOR.saturday : PICKER_COLOR.text)};
  background: ${({ picked }) => (picked ? PICKER_COLOR.primary : 'transparent')};
  box-shadow: ${({ today, picked }) => (today && !picked ? `inset 0 0 0 1.5px ${PICKER_COLOR.primary}` : 'none')};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  /* 못 고르는 날 — 수정 화면의 지난 저장값은 고른 원을 그대로 보여 준다 */
  &:disabled {
    color: ${({ picked }) => (picked ? '#fff' : CAL_COLOR.off)};
    cursor: default;
  }

  /* 이미 넣은 회차 날짜 점 */
  &::after {
    content: '';
    display: ${({ dot }) => (dot ? 'block' : 'none')};
    position: absolute;
    bottom: -3px;
    left: 50%;
    width: 4px;
    height: 4px;
    margin-left: -2px;
    border-radius: 2px;
    background: ${({ picked }) => (picked ? PICKER_COLOR.primaryDeep : PICKER_COLOR.primary)};
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: ${({ picked }) => (picked ? PICKER_COLOR.primary : PICKER_COLOR.primaryCard)};
    }
  }

  &:focus-visible {
    outline: 2px solid ${PICKER_COLOR.primary};
    outline-offset: 1px;
  }

  [data-popover-narrow] & {
    width: 30px;
    height: 30px;
    font-size: 14px;
  }
`;

export interface PickerCalendarProps {
  /** 보이는 달(아무 날이나 — 그 달 1일로 본다) */
  month: Date;
  onMonthChange: (month: Date) => void;
  /** 고른 날(원) */
  isPicked: (date: string) => boolean;
  /** 못 고르는 날(흐리게) */
  isDisabled: (date: string) => boolean;
  onPick: (date: string) => void;
  /** 기간 띠 — 시작·종료가 모두 있을 때만 */
  rangeStart?: string | null;
  rangeEnd?: string | null;
  /** 점 표시할 날(이미 넣은 회차) */
  dots?: string[];
  /** 한 해씩 넘기는 버튼(« ») — 몇 년 전 날짜를 고르는 칸(이력서 경력 기간)만. 기본 없음 */
  yearNav?: boolean;
}

/** 월 버튼 묶음 — 해 넘김 버튼이 있을 때 ‹ 와 « 사이 4px(UI 가이드 4.1) */
const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/** 한 달 달력 — 월 이동 ‹ ›, 오늘 테두리, 고른 날 청록 원, 못 고르는 날 흐리게 */
const PickerCalendar: React.FC<PickerCalendarProps> = ({ month, onMonthChange, isPicked, isDisabled, onPick, rangeStart, rangeEnd, dots, yearNav = false }) => {
  const today: string = formatDateOnly(new Date());
  const cells: Array<string | null> = monthCells(month);
  const prevMonth: Date = addMonths(month, -1);
  const prevLastDay: string = formatDateOnly(new Date(month.getFullYear(), month.getMonth(), 0));
  // 이전 달의 마지막 날도 못 고르면 그 앞은 볼 일이 없다
  const canGoPrev: boolean = !isDisabled(prevLastDay);
  // 한 해 앞 같은 달의 마지막 날도 못 고르면 해 넘김도 막는다
  const prevYearLastDay: string = formatDateOnly(new Date(month.getFullYear() - 1, month.getMonth() + 1, 0));
  const canGoPrevYear: boolean = !isDisabled(prevYearLastDay);
  const hasRange: boolean = !!rangeStart && !!rangeEnd && rangeStart < rangeEnd;

  const bandOf = (date: string): BandSide => {
    if (!hasRange || !rangeStart || !rangeEnd) return 'none';
    if (date === rangeStart) return 'right';
    if (date === rangeEnd) return 'left';
    return date > rangeStart && date < rangeEnd ? 'full' : 'none';
  };

  return (
    <div>
      <MonthRow>
        <NavGroup>
          {yearNav && (
            <MonthButton type="button" aria-label="이전 해" disabled={!canGoPrevYear} onClick={() => onMonthChange(addMonths(month, -12))}>
              <KeyboardDoubleArrowLeftIcon aria-hidden />
            </MonthButton>
          )}
          <MonthButton type="button" aria-label="이전 달" disabled={!canGoPrev} onClick={() => onMonthChange(prevMonth)}>
            <ChevronLeftIcon aria-hidden />
          </MonthButton>
        </NavGroup>
        <b aria-live="polite">{`${month.getFullYear()}년 ${month.getMonth() + 1}월`}</b>
        <NavGroup>
          <MonthButton type="button" aria-label="다음 달" onClick={() => onMonthChange(addMonths(month, 1))}>
            <ChevronRightIcon aria-hidden />
          </MonthButton>
          {yearNav && (
            <MonthButton type="button" aria-label="다음 해" onClick={() => onMonthChange(addMonths(month, 12))}>
              <KeyboardDoubleArrowRightIcon aria-hidden />
            </MonthButton>
          )}
        </NavGroup>
      </MonthRow>
      <Grid role="group" aria-label={`${month.getFullYear()}년 ${month.getMonth() + 1}월 달력`}>
        {WEEK_LABELS.map((label, dow) => (
          <WeekLabel key={label} dow={dow} aria-hidden>{label}</WeekLabel>
        ))}
        {cells.map((date, index) => {
          if (!date) return <DayCell key={`blank-${index}`} band="none" aria-hidden />;
          const dow: number = index % 7;
          const picked: boolean = isPicked(date);
          const disabled: boolean = isDisabled(date);
          const hasDot: boolean = !!dots?.includes(date);
          return (
            <DayCell key={date} band={bandOf(date)}>
              <DayButton
                type="button"
                dow={dow}
                picked={picked}
                today={date === today}
                dot={hasDot}
                disabled={disabled}
                aria-pressed={picked}
                aria-label={`${formatDayKo(date)}${date === today ? ' 오늘' : ''}${hasDot ? ' 회차 있음' : ''}`}
                onClick={() => onPick(date)}
              >
                {parseLocal(date)?.getDate()}
              </DayButton>
            </DayCell>
          );
        })}
      </Grid>
    </div>
  );
};

// ───────── 연·월 고르기(month 모드) ─────────

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 4px 0 2px;

  [data-popover-narrow] & {
    gap: 6px;
  }
`;

/** 한 달 칸 — 고른 달 청록 바탕, 이번 달 테두리, 못 고르는 달 흐리게(날짜 칸과 같은 색 규칙) */
const MonthCellButton = styled.button<{ picked: boolean; current: boolean }>`
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ picked }) => (picked ? 800 : 500)};
  color: ${({ picked }) => (picked ? '#fff' : PICKER_COLOR.text)};
  background: ${({ picked }) => (picked ? PICKER_COLOR.primary : 'transparent')};
  box-shadow: ${({ current, picked }) => (current && !picked ? `inset 0 0 0 1.5px ${PICKER_COLOR.primary}` : 'none')};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    color: ${({ picked }) => (picked ? '#fff' : CAL_COLOR.off)};
    cursor: default;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: ${({ picked }) => (picked ? PICKER_COLOR.primary : PICKER_COLOR.primaryCard)};
    }
  }

  &:focus-visible {
    outline: 2px solid ${PICKER_COLOR.primary};
    outline-offset: 1px;
  }

  [data-popover-narrow] & {
    height: 36px;
    font-size: 14px;
  }
`;

export interface PickerMonthGridProps {
  /** 보이는 해 */
  year: number;
  onYearChange: (year: number) => void;
  /** 고른 달(`YYYY-MM`) */
  isPicked: (month: string) => boolean;
  /** 못 고르는 달(흐리게) */
  isDisabled: (month: string) => boolean;
  onPick: (month: string) => void;
}

/** 한 해 열두 달 — 해 이동 ‹ ›(달력의 달 이동과 같은 버튼), 3칸 × 4줄 */
export const PickerMonthGrid: React.FC<PickerMonthGridProps> = ({ year, onYearChange, isPicked, isDisabled, onPick }) => {
  const thisMonth: string = monthKeyOfDate(new Date());
  const months: string[] = Array.from({ length: 12 }, (_, i) => monthKey(year, i + 1));
  // 앞 해 12월도 못 고르면 그 앞은 볼 일이 없다(뒤 해도 같다)
  const canGoPrev: boolean = !isDisabled(monthKey(year - 1, 12));
  const canGoNext: boolean = !isDisabled(monthKey(year + 1, 1));
  return (
    <div>
      <MonthRow>
        <MonthButton type="button" aria-label="이전 해" disabled={!canGoPrev} onClick={() => onYearChange(year - 1)}>
          <ChevronLeftIcon aria-hidden />
        </MonthButton>
        <b aria-live="polite">{`${year}년`}</b>
        <MonthButton type="button" aria-label="다음 해" disabled={!canGoNext} onClick={() => onYearChange(year + 1)}>
          <ChevronRightIcon aria-hidden />
        </MonthButton>
      </MonthRow>
      <MonthGrid role="group" aria-label={`${year}년 달 고르기`}>
        {months.map((key) => {
          const picked: boolean = isPicked(key);
          return (
            <MonthCellButton
              key={key}
              type="button"
              picked={picked}
              current={key === thisMonth}
              disabled={isDisabled(key)}
              aria-pressed={picked}
              aria-label={`${formatMonthKo(key)}${key === thisMonth ? ' 이번 달' : ''}`}
              onClick={() => onPick(key)}
            >
              {`${Number(key.slice(5, 7))}월`}
            </MonthCellButton>
          );
        })}
      </MonthGrid>
    </div>
  );
};

export default PickerCalendar;
